// テスト・シミュレーター用の loadout（meta が無くても戦闘を回せるように）。
// tier ≒ 章：tier N は N章の5面を「推奨戦力ちょうど」の育成で遊んでいるくらいの強さ（N≧1）。
// HP・攻撃は敵の基準曲線 CFG.curve（meta の想定育成から作ったもの）で伸ばすので、meta の standardLoadout にほぼそろう
// （戦力 / 推奨戦力 ≒ 1.0。tools/sim の battle-sim --meta-power が本来の較正で、こちらはテスト用の目安）。
import type { BattleLoadout, BuildingType, CharId } from '../types';
import { CHARS } from '../data/chars';
import { curveAt } from './stages';

const ORDER: BuildingType[] = ['yagura', 'sakaku', 'nobori', 'otoshiana', 'shuzen', 'torii', 'kitsunebi', 'wara', 'horoku', 'ozutsu'];
/** ステージ1の推奨戦力ちょうどの育成の HP・攻撃（キャラの基礎値に対する割合） */
const S1 = { hp: 0.88, atk: 1.03 };

/** tier に対応するステージ（N章の5面。tier 0 はステージ1） */
export const testStage = (tier: number) => (tier <= 0 ? 1 : Math.min(100, (tier - 1) * 10 + 5));

export const testLevel = (tier: number) => (tier <= 0 ? 1 : 9 * tier - 3);

export function testLoadout(charId: CharId, tier = 1): BattleLoadout {
  const t = Math.max(0, Math.min(12, Math.floor(tier)));
  const ch = CHARS[charId];
  const level = testLevel(t);
  const s = testStage(t);
  // 10章より上の tier は1段ごとに1割ずつ
  const extra = 1 + 0.1 * Math.max(0, t - 10);
  const hpC = curveAt(s, 'hp') * extra;
  const atkC = curveAt(s, 'atk') * extra;
  const hp = Math.round(ch.base.hp * S1.hp * hpC);
  // 普請・薬・倍率などは meta の想定育成（standardLoadout）のおおよその値に合わせる
  const u = Math.max(1, t);
  const unlocked = ORDER.slice(0, [3, 3, 3, 3, 4, 4, 4, 4, 4, 5, 5, 6][u - 1]);
  const blv = Math.round(1.2 * (u - 1));
  return {
    charId,
    level,
    stats: {
      hp,
      atk: Math.round(ch.base.atk * S1.atk * atkC),
      def: Math.round(ch.base.def * Math.pow(hpC, 0.85)),
      agi: Math.round(ch.base.agi * (1 + 0.0045 * (s - 1))),
      move: ch.base.move,
      range: ch.base.range,
    },
    skill: { power: 1 + 0.018 * (u - 1), cdDelta: 0, splashBonus: u >= 6 ? 0.16 : u >= 3 ? 0.08 : 0 },
    build: {
      unlocked,
      levels: Object.fromEntries(unlocked.map((b) => [b, blv])),
      maxCount: Math.min(5, 2 + Math.ceil((u - 1) * 0.6)),
      perTurn: u >= 4 ? 2 : 1,
      costMult: 1 - 0.016 * (u - 1),
      hpMult: 1 + 0.045 * (u - 1),
      powerMult: 1 + 0.02 * (u - 1),
      range: u >= 8 ? 2 : 1,
    },
    materials: { start: 4 + Math.min(3, Math.floor(u / 3)), incomeBonus: u >= 8 ? 1 : 0 },
    potions: { count: u >= 7 ? 3 : 2, heal: u >= 7 ? 0.575 : u >= 3 ? 0.5 : 0.35 },
    diceMin: 1,
    respawnDelta: u >= 8 ? -1 : 0,
    territoryBonus: u >= 2 ? 0.06 : 0,
    mult: { core: 1 + 0.03 * (u - 1), boss: u >= 7 ? 1.05 : 1, neutral: 1 + 0.014 * (u - 1), taken: u >= 4 ? 0.95 : 1 },
    lifesteal: 0,
    flags: [],
  };
}
